import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PageLayout from "../../components/layout/PageLayout";
import LicitacionMenu from "./LicitacionMenu";
import { useUser } from "../../Context/UserContext";
import categories from "../../Data/Categories";
import companyData from "../../Data/CompanyData";
import { Helmet } from "react-helmet-async";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const handleCardKeyDown = (event, onSelect) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    onSelect();
  }
};

const CreateTenderStep1 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();

  // Incluir formData completo desde el estado
  const initialData = location.state?.formData || {};
  const [showExtraOptions, setShowExtraOptions] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [visibleOptions, setVisibleOptions] = useState(10);

  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  const handleCategorySelection = (category) => {
    setSelectedCategory(category);
    if (category === "Otro Servicio") {
      setShowExtraOptions(true);
      setSelectedSubCategory("");
    } else {
      setShowExtraOptions(false);
      setSelectedSubCategory("");
    }
  };

  const handleSubCategorySelection = (subCategory) => {
    setSelectedSubCategory(subCategory);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedCategory === "Otro Servicio" && !selectedSubCategory) {
      toast.warning("Por favor selecciona una subcategoría.");
      return;
    }

    const finalCategory =
      selectedCategory === "Otro Servicio"
        ? selectedSubCategory
        : selectedCategory;

    navigate("/create-tender-step-2", {
      state: { formData: { ...initialData, categoria: finalCategory } },
    });
  };

  // Filtrar categorías de companyData que no estén en categories
  const extraCategories = companyData.sectores.filter(
    (sector) => !categories.some((cat) => cat.label === sector.label)
  );

  // Restaurar la selección cuando initialData.categoria esté presente
  useEffect(() => {
    if (initialData.categoria) {
      const isExtraCategory = extraCategories.some(
        (sector) => sector.label === initialData.categoria
      );

      if (isExtraCategory) {
        setShowExtraOptions(true);
        setSelectedCategory("Otro Servicio");
        setSelectedSubCategory(initialData.categoria);
      } else {
        setSelectedCategory(initialData.categoria);
        setSelectedSubCategory("");
      }
    }
  }, []); // 👈 Se ejecuta SOLO en el montaje

  return (
    <PageLayout>
      <Helmet>
        <title>Crear Licitación - Paso 1 | Procu</title>
        <meta
          name="description"
          content="Selecciona el tipo de servicio que necesitas contratar en Procu. Conéctate con proveedores confiables y accede a oportunidades de negocio."
        />
        <meta name="keywords" content="crear licitación, proveedores, contratación, servicios, negocios, Procu, B2B" />
      </Helmet>

      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <LicitacionMenu currentStep={1} />

        <h3 className="text-xl font-semibold text-foreground">Selecciona un Servicio</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          ¿Qué tipo de servicio necesitas contratar? Te conectaremos con nuestra amplia base de proveedores.
        </p>
        <form onSubmit={handleSubmit} className="mt-6">
          <div className="grid gap-3 sm:grid-cols-2">
            {categories.map((category) => (
              <div
                key={category.label}
                role="button"
                tabIndex={0}
                aria-pressed={selectedCategory === category.label}
                onClick={() => handleCategorySelection(category.label)}
                onKeyDown={(e) => handleCardKeyDown(e, () => handleCategorySelection(category.label))}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-lg border border-border p-4 hover:bg-muted",
                  selectedCategory === category.label && "border-primary bg-primary/5"
                )}
              >
                <span className="text-2xl">{category.icon}</span>
                <div>
                  <h4 className="text-sm font-semibold text-foreground">{category.label}</h4>
                  <p className="text-xs text-muted-foreground">{category.description}</p>
                </div>
              </div>
            ))}
          </div>

          {showExtraOptions && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-foreground">Selecciona una subcategoría:</h4>
              <div className="mt-2 flex flex-wrap gap-2">
                {extraCategories.slice(0, visibleOptions).map((subCategory) => (
                  <div
                    key={subCategory.label}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selectedSubCategory === subCategory.label}
                    onClick={() => handleSubCategorySelection(subCategory.label)}
                    onKeyDown={(e) => handleCardKeyDown(e, () => handleSubCategorySelection(subCategory.label))}
                    className={cn(
                      "flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted",
                      selectedSubCategory === subCategory.label && "border-primary bg-primary/5 text-primary"
                    )}
                  >
                    <span>{subCategory.icon}</span>
                    <span>{subCategory.label}</span>
                  </div>
                ))}
                {visibleOptions < extraCategories.length && (
                  <button
                    onClick={(e) => {
                      e.preventDefault(); // Evitar que el formulario se intente enviar
                      setVisibleOptions((prev) => prev + 10);
                    }}
                    className="rounded-md border border-dashed border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
                  >
                    Ver más
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="mt-8">
            <Button
              type="submit"
              disabled={
                !selectedCategory ||
                (selectedCategory === "Otro Servicio" && !selectedSubCategory)
              }
            >
              Siguiente
            </Button>
          </div>
        </form>
      </div>
    </PageLayout>
  );
};

export default CreateTenderStep1;
